import { IconRefresh, IconTriangle } from "@tabler/icons-react"
import { Component, type ErrorInfo, type FC, type ReactNode, useState } from "react"
import styled from "styled-components"

import Button from "~/components/Button"
import LoadingIndicator from "~/components/LoadingIndicator"

export default class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
	state = { hasError: false }

	static getDerivedStateFromError() {
		return { hasError: true }
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		reportError(error, { componentStack: errorInfo.componentStack })
	}

	render() {
		if (!this.state.hasError) return this.props.children

		return <ErrorFallback />
	}
}

export const RouteErrorBoundary: FC = () => <ErrorFallback />

export function reportError(error: unknown, extra?: Record<string, unknown>) {
	if (CONFIG.isDevelopment) {
		console.error(error, extra)
		return
	}

	import("@sentry/react").then(Sentry => {
		Sentry.captureException(error instanceof Error ? error : new Error(String(error)), { extra })
	})
}

const ErrorFallback: FC = () => {
	const [isRetrying, setRetrying] = useState(false)

	return (
		<Container>
			<ErrorCard>
				<IconContainer>
					<IconTriangle />
				</IconContainer>
				<Title>Oops! Something went wrong</Title>
				<Description>
					We're sorry this happened. A report of this error has been sent to our development team.
					<br />
					If you continue experiencing problems, please contact us through the live chat.
				</Description>
				<Button
					variant="primary"
					isDisabled={isRetrying}
					onClick={() => {
						setRetrying(true)
						setTimeout(() => window.location.reload(), 0)
					}}
				>
					{isRetrying ? <LoadingIndicator size={24} /> : <IconRefresh className="size-6" />}
					{isRetrying ? "Reloading..." : "Reload"}
				</Button>
			</ErrorCard>
		</Container>
	)
}

const Container = styled.div`
	width: 100%;
	min-height: 100vh;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 24px;
	background: linear-gradient(135deg, #faf9f7 0%, #f5f4f2 100%);
`

const ErrorCard = styled.div`
	background: white;
	border-radius: 16px;
	padding: 48px 32px;
	text-align: center;
	max-width: 480px;
	width: 100%;
	box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
	border: 1px solid #eaeaea;
	display: flex;
	flex-direction: column;
	align-items: center;
`

const IconContainer = styled.div`
	width: 80px;
	height: 80px;
	margin: 0 auto 24px;
	background: linear-gradient(135deg, #fff5f5 0%, #fed7d7 100%);
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #e53e3e;
	font-size: 32px;
`

const Title = styled.h1`
	font-weight: 700;
	font-size: 28px;
	color: #151d48;
	margin: 0 0 16px;
	line-height: 1.2;
`

const Description = styled.p`
	font-size: 16px;
	line-height: 1.6;
	color: #737791;
	margin: 0 0 32px;
`
